import { format, parse, parseISO } from 'date-fns';

import { DayOfWeek, TimeOfDay } from './DayFilters/DayFilters.helpers';

import { STATE_NAME_BY_CODE } from '@shared-components-web';
import { Maybe } from '@shared-components-web/types';
import { CustomAttributeKeys } from '@src/constants';
import { BffFacility } from '@src/lib/__generated/beffe/sdk';
import { AvailableTimeSlot, Location } from '@src/lib/__generated/vetspire/sdk';
import { findCustomLocationAttribute } from '@src/utils/find-custom-location-attribute';

export interface TBffFacilityToVetspire extends Location {
	//Extends Vetspire Location type as distance exists on BFF but not Vetspire sdk
	distance?: Maybe<number>;
}

export type TFilters = {
	timeOfDay: TimeOfDay;
	weekDays: DayOfWeek[];
};

export const THREE_DAYS = 3;

export const SIX_DAYS = 6;

export const SEVEN_DAYS = 7;

export const TEN_DAYS = 10;

export const THIRTY_DAYS = 30;

export const DEFAULT_TIME_SLOT_DURATION = 10;

export const defaultFilters = { timeOfDay: TimeOfDay.All, weekDays: [] };

export const getNewDateFromToday = (days: number, date?: Date) => {
	const today = date ? new Date(date) : new Date();
	return new Date(today.setDate(today.getDate() + days));
};

export const getSlotTimeOfDay = (slot: AvailableTimeSlot): TimeOfDay => {
	if (!slot.time) return TimeOfDay.All;
	const date = parse(slot.time, 'HH:mm', new Date());
	return format(date, 'a').toUpperCase() as TimeOfDay;
};

export const getSlotDayOfWeek = (slot: AvailableTimeSlot): DayOfWeek | null => {
	if (!slot.date) return null;
	return format(parseISO(slot.date), 'EEEE') as DayOfWeek;
};

export const mapBffFacilityToVetspire = (facility: BffFacility): TBffFacilityToVetspire => ({
	id: facility.code.split('_')[1],
	name: facility.name ?? '',
	displayName: facility.displayName,
	addressFields: {
		city: facility.address?.city,
		line1: facility.address?.address1,
		line2: facility.address?.address2,
		postalCode: facility.address?.zipCode,
		state: facility.address?.stateCode ? STATE_NAME_BY_CODE[facility.address?.stateCode] : null,
	},
	phoneNumber: facility.phoneNumber,
	latitude: facility.location?.latitude?.toString(),
	longitude: facility.location?.longitude?.toString(),
	distance: facility.location?.distance,
	timezone: facility.location?.timezoneIANA ?? facility.location?.timeZone,
	enableCovetrus: false,
	enableCubexTwoWay: false,
	attributes: facility.attributes?.map((attr) => ({
		customAttribute: {
			name: attr?.name,
			id: attr?.id,
		},
		value: attr?.value,
	})),
});

export function getHospitalName(loc?: Location | null): string {
	if (!loc) return '';
	const hospitalNameKey = CustomAttributeKeys?.hospitalName ?? 'Hospital Name';
	return findCustomLocationAttribute(hospitalNameKey, loc) || loc.displayName || '';
}

export function hasMidWeekPromotionEnabled(loc?: Location | null): boolean {
	if (!loc) return false;
	const midWeekPromotionKey = CustomAttributeKeys?.midWeekPromotion ?? 'Mid Week Promotion';
	const midWeekPromotionValue = findCustomLocationAttribute(midWeekPromotionKey, loc);
	const normalizedValue = midWeekPromotionValue?.trim().toLowerCase();
	return normalizedValue === 'yes' || normalizedValue === 'true';
}
